// Recurrence on one axis: the same conduct raised again on a later turn.
//
// The pipeline judges a session window by window and raises one finding per
// occurrence, under whatever title the judge chose that time; one repeated
// command drew 29 wordings on a staging trace. The rows are right, each is a
// distinct act. The READING is wrong when thirty of them list as thirty
// unrelated findings. This module folds a trace's findings into incidents by
// what they cite: two findings whose quotes share enough 5-character shingles
// are the same conduct recurring.
//
// Thresholds come from 52 hand-labelled pairs across a week of staging
// traces. Per-citation shingle Jaccard placed no wrong pair at 0.5 or above
// and kept a habit together through 16 different clippings of one command.
// Between 0.3 and 0.5 it is right more often than not but merges "kubectl set
// env" with "kubectl set image", so that band is RELATED: drawn, not merged.
// Below 0.3 lies shared shell boilerplate, and nothing is said.
//
// Grouping is first-match in turn order: a finding joins the first incident it
// resembles above the hard bar, so an incident's identity is its earliest
// member and does not change when later findings arrive. Nothing about the
// rows changes. This is a view, and it can be wrong without losing anything.
import { incidentTone } from "./traceReview.js";

export const INCIDENT_HARD = 0.5;
export const INCIDENT_SOFT = 0.3;
// Past this many located findings the pairwise pass is skipped and every
// finding stands alone: a timeline that dense is a different reading problem.
export const INCIDENT_CAP = 250;
const K = 5;
const TONE_RANK = { danger: 2, warn: 1, calm: 0 };

export function shinglesOf(text) {
    const t = String(text ?? "").replace(/\s+/g, " ").trim().toLowerCase();
    const out = new Set();
    for (let i = 0; i + K <= t.length; i++) out.add(t.slice(i, i + K));
    return out;
}

export function shingleJaccard(a, b) {
    if (!a.size || !b.size) return 0;
    let inter = 0;
    for (const s of a) if (b.has(s)) inter += 1;
    return inter / (a.size + b.size - inter);
}

// The strongest resemblance between any citation of one finding and any of
// another. Per citation, not over a finding's pooled quotes: a finding that
// also cites an unrelated status line must still match its habit.
const SHINGLES = new WeakMap();
function locatedShingles(inc) {
    if (!SHINGLES.has(inc)) SHINGLES.set(inc, (inc.citations || []).filter((c) => c.verified !== "missing" && c.quote).map((c) => shinglesOf(c.quote)));
    return SHINGLES.get(inc);
}
export function findingSimilarity(a, b) {
    return bestSimilarity(locatedShingles(a), locatedShingles(b));
}

// The citation of `b` that most resembles any citation of `a`, with its score:
// the quote a hover shows as "this conduct, there", chosen so it is the
// occurrence's own words for the same act rather than whatever else it cited.
export function closestCitation(a, b) {
    const mine = locatedShingles(a);
    let best = null;
    (b.citations || []).forEach((c) => {
        if (c.verified === "missing" || !c.quote) return;
        const sb = shinglesOf(c.quote);
        let score = 0;
        for (const sa of mine) score = Math.max(score, shingleJaccard(sa, sb));
        if (!best || score > best.score) best = { quote: c.quote, score, turn: c.locatedTurn != null ? c.locatedTurn - 1 : null };
    });
    return best;
}
function bestSimilarity(a, b) {
    let best = 0;
    for (const sa of a) for (const sb of b) {
        const s = shingleJaccard(sa, sb);
        if (s > best) best = s;
    }
    return best;
}

// -> { incidents, related, byFinding, incidentOf }
//   incidents: [{ index, members, turns (0-based, sorted), from, through, count, tone, title }]
//   related:   [{ a, b, score }] incident index pairs in the soft band
//   byFinding: Map(finding id -> incident index); incidentOf: Map(finding id -> incident)
export function incidentsOf(report, { hard = INCIDENT_HARD, soft = INCIDENT_SOFT, cap = INCIDENT_CAP } = {}) {
    const placed = (report?.incidents || [])
        .filter((inc) => !inc.uncited && inc.turns?.length)
        .map((inc) => ({ inc, first: Math.min(...inc.turns) - 1 }))
        .sort((x, y) => x.first - y.first);
    const groups = [];
    const relatedScore = new Map();
    const shinglesFor = locatedShingles;
    for (const { inc } of placed) {
        let home = null;
        const soften = [];
        if (placed.length <= cap) {
            const mine = shinglesFor(inc);
            for (const g of groups) {
                let s = 0;
                for (const m of g.members) {
                    s = Math.max(s, bestSimilarity(mine, shinglesFor(m)));
                    if (s >= hard) break;
                }
                if (s >= hard) { home = g; break; }
                if (s >= soft) soften.push([g.index, s]);
            }
        }
        if (home) home.members.push(inc);
        else {
            home = { index: groups.length, members: [inc] };
            groups.push(home);
        }
        for (const [other, s] of soften) {
            if (other === home.index) continue;
            const key = other < home.index ? `${other}:${home.index}` : `${home.index}:${other}`;
            relatedScore.set(key, Math.max(relatedScore.get(key) || 0, s));
        }
    }
    const incidents = groups.map((g) => {
        const turns = [...new Set(g.members.flatMap((m) => m.turns.map((t) => t - 1)))].sort((a, b) => a - b);
        const titles = new Map();
        for (const m of g.members) titles.set(m.title, (titles.get(m.title) || 0) + 1);
        const title = [...titles.entries()].sort((x, y) => y[1] - x[1])[0][0];
        const tone = g.members.map(incidentTone).sort((x, y) => (TONE_RANK[y] ?? 0) - (TONE_RANK[x] ?? 0))[0] || "calm";
        return { index: g.index, members: g.members, turns, from: turns[0], through: turns[turns.length - 1], count: g.members.length, tone, title };
    });
    const related = [...relatedScore.entries()].map(([key, score]) => {
        const [a, b] = key.split(":").map(Number);
        return { a, b, score };
    });
    const byFinding = new Map();
    const incidentOf = new Map();
    for (const inc of incidents) for (const m of inc.members) {
        if (m.id != null) { byFinding.set(m.id, inc.index); incidentOf.set(m.id, inc); }
    }
    return { incidents, related, byFinding, incidentOf };
}
